import React, { useState, useEffect, useMemo } from "react";
import useFetch from "../../../../hooks/useFetch";
import {
  Box, Typography,
  Chip,
  IconButton, Select, MenuItem, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tooltip, Snackbar, Alert, TextField, InputAdornment, Paper, TableSortLabel, Checkbox
} from "@mui/material";
import { Edit, Delete, Archive, Unarchive, Search, Clear, Add as AddIcon, ClassOutlined as ClassOutlinedIcon, ArrowForward as BackIcon } from "@mui/icons-material";
import TablePagination from '../UserList/TablePagination';
import { toPersianDigits, toEnglishDigits, formatNumberInputValue, parseNumberInput } from "../../../../utils/convertNumbers";
import useDeleteItem from "../../../../hooks/useDeleteItem";
import { useAuth } from "../../../../context/AuthContext";
import RTL from "../../../../utils/RTL";
import StandardTable from "../../../common/table/StandardTable";
import SearchableAutocomplete from "../../../SearchableAutocomplete";
import api from "../../../../config/axiosConfig";
import CreateTeacherCourseForm from "./CreateTeacherCourseForm";

// Shared height so the toolbar controls (search, filters, actions) line up.
const FIELD_HEIGHT = 40;

export default function AllCoursesList() {
  const { user } = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [courseToArchive, setCourseToArchive] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [activeRegistrationsMap, setActiveRegistrationsMap] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('course_name'); // Default sort by course name
  const [orderDirection, setOrderDirection] = useState('asc'); // 'asc' or 'desc'

  // Bulk fee-update mode state (all local — no global state)
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkFee, setBulkFee] = useState(''); // English-digit numeric string; grouped for display
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Form dialog state
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [selectedTeacherUser, setSelectedTeacherUser] = useState("");
  const [selectedTeacherOption, setSelectedTeacherOption] = useState(null);
  // When editing a course that has active enrollments, only the fee is editable.
  const [feeOnlyEdit, setFeeOnlyEdit] = useState(false);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    teacher: '',
    teacher_status: 'active', // hide courses of archived (deactivated) teachers by default
    course_type: '',
    archive_state: '',
  });
  
  const { deleteItem, loading: deleteLoading } = useDeleteItem();
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page when search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Clear selection when page, sorting, filters, or search changes.
  // Bulk selection is per-page, so it is cleared on the same triggers.
  useEffect(() => {
    setSelectedCourseId(null);
    setSelectedIds(new Set());
  }, [page, rowsPerPage, orderBy, orderDirection, debouncedSearchTerm, filters]);

  // Map frontend field names to backend ordering field names
  const getOrderingField = (fieldName) => {
    const fieldMap = {
      'course_name': 'course_name',
      'teacher': 'teacher__first_name', // Sort by teacher's first name (teacher is ForeignKey to User)
      'course_type': 'course_type',
      'capacity': 'capacity',
      'fee': 'fee',
      'duration': 'duration',
      'registration_sessions': 'registration_sessions',
      'teacher_percentage': 'teacher_percentage',
      'fixed_course_salary': 'fixed_course_salary',
    };
    return fieldMap[fieldName] || fieldName;
  };

  // Handle sort
  const handleSort = (fieldName) => {
    if (orderBy === fieldName) {
      // Toggle direction if same field
      setOrderDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setOrderBy(fieldName);
      setOrderDirection('asc');
    }
    setPage(0); // Reset to first page when sorting changes
  };

  // Memoize params to prevent infinite loops - only recreate when dependencies actually change
  const apiParams = useMemo(() => {
    const orderingField = getOrderingField(orderBy);
    const ordering = orderDirection === 'desc' ? `-${orderingField}` : orderingField;
    
    const params = {
      page: page + 1, // API uses 1-based pagination
      page_size: rowsPerPage,
      ordering: ordering,
      _rt: refreshToken // Refresh token to force refetch
    };
    
    // Add search term
    if (debouncedSearchTerm) {
      params.search = debouncedSearchTerm;
    }
    
    // Add filters
    if (filters.teacher) {
      params.teacher = filters.teacher;
    }
    if (filters.teacher_status && filters.teacher_status !== 'all') {
      params.teacher_status = filters.teacher_status;
    }
    if (filters.course_type) {
      params.course_type = filters.course_type;
    }
    if (filters.archive_state !== '') {
      params.archive_state = filters.archive_state === 'true';
    }
    
    return params;
  }, [page, rowsPerPage, refreshToken, debouncedSearchTerm, filters, orderBy, orderDirection]);

  const { data, loading, error } = useFetch(
    "/operations/api/v1/teachers-and-courses/",
    { 
      params: apiParams, 
      skip: false 
    }
  );

  // Fetch active registrations status for all courses
  const courses = data?.results || [];
  const courseIds = courses.map(c => c.id).join(',');
  useEffect(() => {
    const fetchActiveRegistrations = async () => {
      const newMap = {};
      for (const course of courses) {
        try {
          const response = await api.get(`/operations/api/v1/teachers-and-courses/${course.id}/has-active-registrations/`);
          newMap[course.id] = response.data.has_active_registrations;
        } catch (error) {
          console.error(`Error fetching active registrations for course ${course.id}:`, error);
          newMap[course.id] = false; // Default to false on error
        }
      }
      setActiveRegistrationsMap(newMap);
    };

    if (courses.length > 0) {
      fetchActiveRegistrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIds]);

  // Pagination handlers
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  // Add course handler
  const handleAdd = () => {
    setSelectedCourse(null);
    setSelectedTeacherUser("");
    setSelectedTeacherOption(null);
    setFeeOnlyEdit(false);
    setFormKey(prev => prev + 1);
    setFormDialogOpen(true);
  };

  // Edit course handler
  const handleEditCourse = (course) => {
    // Courses with active enrollments are still editable, but only the fee: it is
    // frozen per enrollment, so a new fee prices future registrations only.
    setFeeOnlyEdit(!!activeRegistrationsMap[course.id]);

    // Set the selected course ID for visual feedback
    setSelectedCourseId(course.id);
    
    // Preserve original shape so the edit form can prefill the teacher autocomplete label
    const teacherId = (course?.teacher && typeof course.teacher === 'object' && course.teacher.id)
      ? Number(course.teacher.id)
      : (typeof course.teacher === 'number')
        ? course.teacher
        : (typeof course.teacher === 'string')
          ? Number(course.teacher)
          : null;

    // Build teacher option object for autocomplete
    const teacherOption = course?.teacher && typeof course.teacher === 'object'
      ? {
          id: course.teacher.id,
          user: {
            id: course.teacher.id,
            first_name: course.teacher.first_name,
            last_name: course.teacher.last_name,
            email: course.teacher.email,
          }
        }
      : null;

    setSelectedCourse(course);
    setSelectedTeacherUser(teacherId || "");
    setSelectedTeacherOption(teacherOption);
    setFormKey(prev => prev + 1);
    setFormDialogOpen(true);
  };

  // Handle form success (after create/edit)
  const handleFormSuccess = (action) => {
    setFormDialogOpen(false);
    setSelectedCourse(null);
    setSelectedCourseId(null);
    setFeeOnlyEdit(false);
    setRefreshToken(prev => prev + 1);
    
    const message = action === 'edit' 
      ? 'دوره با موفقیت ویرایش شد.'
      : 'دوره با موفقیت ثبت شد.';
    
    setSnackbar({
      open: true,
      message,
      severity: 'success'
    });
  };

  // Delete course handlers
  const handleDeleteClick = (course) => {
    // Check if course has active registrations
    if (activeRegistrationsMap[course.id]) {
      setSnackbar({
        open: true,
        message: 'امکان حذف دوره وجود ندارد زیرا هنرجویان در این دوره ثبت‌نام کرده‌اند.',
        severity: 'warning'
      });
      return;
    }
    
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (courseToDelete) {
      try {
        await deleteItem(`/operations/api/v1/teachers-and-courses/${courseToDelete.id}/`);
        setDeleteDialogOpen(false);
        setCourseToDelete(null);
        // Trigger a re-fetch by updating the refresh token
        setRefreshToken(prev => prev + 1);
        // Reset to first page after deletion
        setPage(0);
      } catch (error) {
        console.error('Error deleting course:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCourseToDelete(null);
  };

  // Archive course handlers
  const handleArchiveClick = (course) => {
    setCourseToArchive(course);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (courseToArchive) {
      setArchiveLoading(true);
      try {
        const response = await api.patch(`/operations/api/v1/teachers-and-courses/${courseToArchive.id}/archive/`);
        setSnackbar({
          open: true,
          message: response.data.message || (response.data.archive_state ? 'دوره بایگانی شد.' : 'دوره از بایگانی خارج شد.'),
          severity: 'success'
        });
        setArchiveDialogOpen(false);
        setCourseToArchive(null);
        // Trigger a re-fetch by updating the refresh token
        setRefreshToken(prev => prev + 1);
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'خطا در عملیات بایگانی.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      } finally {
        setArchiveLoading(false);
      }
    }
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
    setCourseToArchive(null);
  };

  // Bulk fee-update handlers
  const exitBulkMode = () => {
    setBulkMode(false);
    setSelectedIds(new Set());
    setBulkFee('');
  };

  const handleToggleBulkMode = () => {
    if (bulkMode) {
      exitBulkMode();
    } else {
      setBulkMode(true);
    }
  };

  const handleToggleSelect = (courseId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
  };

  // Valid fee = positive integer once Persian digits are normalized to English.
  const bulkFeeNumber = Number(toEnglishDigits(bulkFee));
  const isBulkFeeValid = Number.isInteger(bulkFeeNumber) && bulkFeeNumber > 0;

  const handleBulkApplyClick = () => {
    setBulkConfirmOpen(true);
  };

  const handleBulkConfirm = async () => {
    const fee = Number(toEnglishDigits(bulkFee));
    setBulkLoading(true);
    try {
      const response = await api.patch('/operations/api/v1/teachers-and-courses/bulk-update-fee/', {
        course_ids: [...selectedIds],
        fee,
      });
      let message = 'شهریه دوره‌ها با موفقیت به‌روزرسانی شد.';
      if (response.data.not_found_ids && response.data.not_found_ids.length > 0) {
        message += ` (${toPersianDigits(response.data.not_found_ids.length)} مورد یافت نشد یا خارج از موسسه بود.)`;
      }
      setSnackbar({ open: true, message, severity: 'success' });
      setRefreshToken(prev => prev + 1);
      setBulkMode(false);
      setSelectedIds(new Set());
      setBulkFee('');
      setBulkConfirmOpen(false);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'خطا در به‌روزرسانی شهریه دوره‌ها.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setBulkLoading(false);
    }
  };


  const totalCount = data?.count || 0;
  const hasActiveFilters = debouncedSearchTerm || filters.teacher || filters.teacher_status !== 'active' || filters.course_type || filters.archive_state !== '';

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      teacher: '',
      teacher_status: 'active',
      course_type: '',
      archive_state: '',
    });
    setPage(0);
  };

  return (
    <RTL>
      <Box sx={{ p: 2 }}>
      {formDialogOpen ? (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Button startIcon={<BackIcon />} variant="outlined" onClick={() => { setFormDialogOpen(false); setSelectedCourse(null); setSelectedCourseId(null); setFeeOnlyEdit(false); }}>
              بازگشت به فهرست
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {selectedCourse ? (feeOnlyEdit ? 'ویرایش شهریه و حق‌الزحمه دوره' : 'ویرایش دوره') : 'افزودن دوره'}
            </Typography>
          </Box>
          <Paper sx={{ p: 3 }}>
            <CreateTeacherCourseForm
              formKey={formKey}
              userRole={user?.staff_role?.name || (user?.roles?.[0] ?? "")}
              onTeacherChange={setSelectedTeacherUser}
              mode={selectedCourse ? "edit" : "create"}
              editRecord={selectedCourse}
              selectedTeacherUser={selectedTeacherUser}
              initialTeacherOption={selectedTeacherOption}
              onAfterSubmit={handleFormSuccess}
              feeOnly={feeOnlyEdit}
            />
          </Paper>
        </Box>
      ) : (
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Header: title + count, primary action */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ display: 'flex', color: 'primary.main' }}><ClassOutlinedIcon fontSize="small" /></Box>
            <Typography variant="h6" sx={{ m: 0, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              فهرست دوره‌ها
            </Typography>
            {totalCount > 0 && (
              <Chip size="small" variant="outlined" color="primary" label={toPersianDigits(totalCount)} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={bulkMode ? 'contained' : 'outlined'}
              onClick={handleToggleBulkMode}
              sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
            >
              تغییر شهریه به صورت گروهی
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
            >
              افزودن دوره
            </Button>
          </Box>
        </Box>

        {/* Bulk fee-update action bar */}
        {bulkMode && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap', mt: 1 }}>
              {`${toPersianDigits(selectedIds.size)} دوره انتخاب شده`}
            </Typography>
            <TextField
              size="small"
              label="شهریه جدید (تومان)"
              inputMode="numeric"
              value={formatNumberInputValue(bulkFee)}
              onChange={(e) => {
                const parsed = parseNumberInput(e.target.value, false);
                setBulkFee(parsed === '' ? '' : String(parsed));
              }}
              helperText="شهریه جدید فقط برای ثبت‌نام‌های بعدی اعمال می‌شود؛ هنرجویان فعلی با همان شهریه قبلی ادامه می‌دهند."
              sx={{ minWidth: 260 }}
              InputProps={{ sx: { height: FIELD_HEIGHT } }}
            />
            <Button
              variant="contained"
              onClick={handleBulkApplyClick}
              disabled={selectedIds.size === 0 || !isBulkFeeValid}
              sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
            >
              اعمال شهریه جدید
            </Button>
            <Button
              variant="outlined"
              onClick={exitBulkMode}
              sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
            >
              انصراف
            </Button>
          </Box>
        )}

        {/* Toolbar: search + filters (flat, unified height) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="جستجوی نام دوره..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ m: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: FIELD_HEIGHT },
              }}
            />
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <SearchableAutocomplete
              apiUrl="/accounts/api/v1/profile/teachers/"
              label="آموزگار"
              placeholder="انتخاب آموزگار..."
              value={filters.teacher}
              onChange={(value) => handleFilterChange('teacher', value || '')}
              getOptionLabel={(option) => {
                if (!option) return '';
                const user = option?.user;
                if (!user) return '';
                const first = user.first_name?.trim() || '';
                const last = user.last_name?.trim() || '';
                const full = `${first} ${last}`.trim();
                return full || `آموزگار #${option.id}`;
              }}
              getOptionValue={(option) => option?.user?.id}
              size="small"
            />
          </Box>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filters.course_type}
              onChange={(e) => handleFilterChange('course_type', e.target.value)}
              displayEmpty
              sx={{ height: FIELD_HEIGHT }}
            >
              <MenuItem value="">همه انواع</MenuItem>
              <MenuItem value="individual">خصوصی</MenuItem>
              <MenuItem value="group">گروهی</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filters.archive_state}
              onChange={(e) => handleFilterChange('archive_state', e.target.value)}
              displayEmpty
              sx={{ height: FIELD_HEIGHT }}
            >
              <MenuItem value="">همه وضعیت‌ها</MenuItem>
              <MenuItem value="false">فعال</MenuItem>
              <MenuItem value="true">بایگانی</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={filters.teacher_status}
              onChange={(e) => handleFilterChange('teacher_status', e.target.value)}
              displayEmpty
              sx={{ height: FIELD_HEIGHT }}
            >
              <MenuItem value="active">آموزگاران فعال</MenuItem>
              <MenuItem value="inactive">آموزگاران غیرفعال</MenuItem>
              <MenuItem value="all">همه آموزگاران</MenuItem>
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Button
              startIcon={<Clear />}
              onClick={handleClearFilters}
              size="small"
              variant="outlined"
              sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
            >
              پاک کردن فیلترها
            </Button>
          )}
        </Box>

      {
        (() => {
          const columns = [
            { 
              id: "course_name", 
              label: "عنوان دوره",
              align: "start",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'course_name'}
                  direction={orderBy === 'course_name' ? orderDirection : 'asc'}
                  onClick={() => handleSort('course_name')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  عنوان دوره
                </TableSortLabel>
              ),
              render: (course) => (
                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                  {course.course_name}
                </Typography>
              ) 
            },
            { 
              id: "teacher", 
              label: "آموزگار",
              align: "start",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'teacher'}
                  direction={orderBy === 'teacher' ? orderDirection : 'asc'}
                  onClick={() => handleSort('teacher')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  آموزگار
                </TableSortLabel>
              ),
              render: (course) => (
                <Typography variant="body2">
                  {course.teacher ? `${course.teacher.first_name || ''} ${course.teacher.last_name || ''}`.trim() : '—'}
                </Typography>
              ) 
            },
            { 
              id: "course_type",
              label: "نوع دوره",
              priority: "secondary",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'course_type'}
                  direction={orderBy === 'course_type' ? orderDirection : 'asc'}
                  onClick={() => handleSort('course_type')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  نوع دوره
                </TableSortLabel>
              ),
              render: (course) => (
                <Chip 
                  label={course.course_type === "group" ? "گروهی" : "خصوصی"}
                  size="small"
                  color={course.course_type === "group" ? "primary" : "secondary"}
                  variant="outlined"
                />
              ) 
            },
            { 
              id: "capacity",
              label: "ظرفیت",
              priority: "secondary",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'capacity'}
                  direction={orderBy === 'capacity' ? orderDirection : 'asc'}
                  onClick={() => handleSort('capacity')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  ظرفیت
                </TableSortLabel>
              ),
              render: (course) => (course.capacity != null ? toPersianDigits(course.capacity) : "—") 
            },
            { 
              id: "fee", 
              label: "شهریه",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'fee'}
                  direction={orderBy === 'fee' ? orderDirection : 'asc'}
                  onClick={() => handleSort('fee')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  شهریه
                </TableSortLabel>
              ),
              render: (course) => (
                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                  {toPersianDigits(new Intl.NumberFormat('fa-IR').format(Number(course.fee) || 0))}
                </Typography>
              )
            },
            { 
              id: "duration",
              label: "مدت (دقیقه)",
              priority: "secondary",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'duration'}
                  direction={orderBy === 'duration' ? orderDirection : 'asc'}
                  onClick={() => handleSort('duration')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  مدت (دقیقه)
                </TableSortLabel>
              ),
              render: (course) => toPersianDigits(course.duration) 
            },
            { 
              id: "registration_sessions",
              label: "جلسات هر ثبت‌نام",
              priority: "secondary",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'registration_sessions'}
                  direction={orderBy === 'registration_sessions' ? orderDirection : 'asc'}
                  onClick={() => handleSort('registration_sessions')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  جلسات هر ثبت‌نام
                </TableSortLabel>
              ),
              render: (course) => toPersianDigits(course.registration_sessions) 
            },
            { 
              id: "teacher_percentage",
              label: "درصد حق‌الزحمه",
              priority: "secondary",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'teacher_percentage'}
                  direction={orderBy === 'teacher_percentage' ? orderDirection : 'asc'}
                  onClick={() => handleSort('teacher_percentage')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  درصد حق‌الزحمه
                </TableSortLabel>
              ),
              render: (course) => `${toPersianDigits(course.teacher_percentage)}%` 
            },
            { 
              id: "fixed_course_salary",
              label: "حقوق ثابت دوره",
              priority: "secondary",
              renderHeader: (
                <TableSortLabel
                  active={orderBy === 'fixed_course_salary'}
                  direction={orderBy === 'fixed_course_salary' ? orderDirection : 'asc'}
                  onClick={() => handleSort('fixed_course_salary')}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important'
                    }
                  }}
                >
                  حقوق ثابت دوره
                </TableSortLabel>
              ),
              render: (course) => {
                const formatCurrency = (amount) => {
                  if (!amount || amount === 0) return "—";
                  return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount)) + " تومان";
                };
                return (
                  <Typography variant="body2" sx={{ fontWeight: course.fixed_course_salary ? "medium" : "normal" }}>
                    {formatCurrency(course.fixed_course_salary)}
                  </Typography>
                );
              } 
            },
            { id: "status", label: "وضعیت", compact: true, render: (course) => (
              <Chip 
                label={course.archive_state ? "بایگانی" : "فعال"}
                size="small"
                color={course.archive_state ? "default" : "success"}
                variant="outlined"
              />
            ) },
            { id: "actions", label: "عملیات", compact: true, render: (course) => {
              const hasActiveRegistrations = activeRegistrationsMap[course.id] || false;
              // Editing stays available with active enrollments, but limited to pricing (fee + pay scheme).
              const canArchive = !hasActiveRegistrations || course.archive_state;
              const canDelete = !hasActiveRegistrations;

              return (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Tooltip title={hasActiveRegistrations ? "ویرایش شهریه و حق‌الزحمه (هنرجویان فعال دارد)" : "ویرایش دوره"}>
                    <span>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCourse(course);
                        }}
                        size="small"
                        color="primary"
                        aria-label="ویرایش دوره"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={course.archive_state ? "خروج از بایگانی" : (canArchive ? "بایگانی دوره" : "امکان بایگانی وجود ندارد (هنرجویان فعال در این دوره ثبت‌نام کرده‌اند)")}>
                    <span>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveClick(course);
                        }}
                        size="small"
                        color={course.archive_state ? "success" : "default"}
                        disabled={!canArchive}
                        aria-label={course.archive_state ? "خروج از بایگانی" : "بایگانی دوره"}
                      >
                        {course.archive_state ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canDelete ? "حذف دوره" : "امکان حذف وجود ندارد (هنرجویان فعال در این دوره ثبت‌نام کرده‌اند)"}>
                    <span>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(course);
                        }}
                        size="small"
                        color="error"
                        disabled={!canDelete}
                        aria-label="حذف دوره"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            } }
          ];

          // In bulk mode, prepend a selection checkbox column. Archived courses
          // are not selectable, so their checkbox is disabled.
          const displayColumns = bulkMode
            ? [
                {
                  id: "select",
                  label: "",
                  render: (course) => (
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{ display: 'flex', justifyContent: 'center' }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(course.id)}
                        disabled={course.archive_state}
                        onChange={() => handleToggleSelect(course.id)}
                        inputProps={{ 'aria-label': 'انتخاب دوره' }}
                      />
                    </Box>
                  ),
                },
                ...columns,
              ]
            : columns;

          return (
            <>
              <StandardTable
                columns={displayColumns}
                rows={courses}
                loading={loading}
                error={error}
                emptyText="هیچ دوره‌ای یافت نشد."
                getRowKey={(r) => r.id}
                ariaLabel="all-courses"
                minWidth={480}
                onRowClick={handleEditCourse}
                getRowProps={(course) => ({
                  selected: selectedCourseId === course.id,
                  sx: selectedCourseId === course.id ? {
                    backgroundColor: 'action.selected',
                  } : undefined
                })}
              />
              <TablePagination
                totalCount={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          );
        })()
      }
      </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          تأیید حذف دوره
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            آیا از حذف دوره &quot;{courseToDelete?.course_name}&quot; اطمینان دارید؟
            <br />
            <strong>این عمل قابل بازگشت نیست.</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            انصراف
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'در حال حذف...' : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
        aria-labelledby="archive-dialog-title"
        aria-describedby="archive-dialog-description"
      >
        <DialogTitle id="archive-dialog-title">
          {courseToArchive?.archive_state ? 'خروج از بایگانی' : 'بایگانی دوره'}
        </DialogTitle>
        <DialogContent>
          <Typography id="archive-dialog-description">
            آیا از {courseToArchive?.archive_state ? 'خارج کردن دوره' : 'بایگانی دوره'} &quot;{courseToArchive?.course_name}&quot; اطمینان دارید؟
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel} color="primary">
            انصراف
          </Button>
          <Button 
            onClick={handleArchiveConfirm} 
            color={courseToArchive?.archive_state ? "success" : "default"}
            variant="contained"
            disabled={archiveLoading}
          >
            {archiveLoading ? 'در حال انجام...' : (courseToArchive?.archive_state ? 'خروج از بایگانی' : 'بایگانی')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Fee Update Confirmation Dialog */}
      <Dialog
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        aria-labelledby="bulk-fee-dialog-title"
        aria-describedby="bulk-fee-dialog-description"
      >
        <DialogTitle id="bulk-fee-dialog-title">
          تأیید تغییر شهریه گروهی
        </DialogTitle>
        <DialogContent>
          <Typography id="bulk-fee-dialog-description">
            آیا از تغییر شهریه {toPersianDigits(selectedIds.size)} دوره انتخاب‌شده به مبلغ {formatNumberInputValue(bulkFee)} تومان اطمینان دارید؟
            <br />
            شهریه جدید فقط برای ثبت‌نام‌های بعدی اعمال می‌شود؛ هنرجویان فعلی با همان شهریه قبلی ادامه می‌دهند.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)} color="primary">
            انصراف
          </Button>
          <Button
            onClick={handleBulkConfirm}
            color="primary"
            variant="contained"
            disabled={bulkLoading}
          >
            {bulkLoading ? 'در حال اعمال...' : 'اعمال شهریه جدید'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </RTL>
  );
}
